import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getMemberById } from '../utils/memberLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';

// Import icons from solid package
import { faCartShopping, faI } from '@fortawesome/free-solid-svg-icons';

// Import icons from brands package
import {
  faTwitch,
  faYoutube,
  faTwitter,
  faDiscord,
  faInstagram,
  faTiktok,
  faBluesky,
} from '@fortawesome/free-brands-svg-icons';

// Add icons to library
library.add(faCartShopping, faTwitch, faYoutube, faTwitter, faDiscord);

// Function to convert string icon names to proper icon objects
const getIconFromString = (iconName) => {
  // If it's already an icon object, return it
  if (typeof iconName === 'object') {
    return iconName;
  }

  // Map of string icon names to their corresponding icon objects
  const iconMap = {
    // Solid icons
    faCartShopping: faCartShopping,

    // Brand icons
    faTwitch: faTwitch,
    faYoutube: faYoutube,
    faTwitter: faTwitter,
    faDiscord: faDiscord,
    faInstagram: faInstagram,
    faTiktok: faTiktok,
    faBluesky: faBluesky,
  };

  // Return the mapped icon or undefined
  return iconMap[iconName];
};

function MemberDetail() {
  const { memberId } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadMember = async () => {
      try {
        const memberData = await getMemberById(memberId);
        setMember(memberData);
        setLoading(false);
      } catch (error) {
        console.error(`Error loading member with id ${memberId}:`, error);
        setError('Member not found');
        setLoading(false);
      }
    };

    loadMember();
  }, []);

  if (loading) {
    return (
      <div className='bg-gray-50 dark:bg-dark-DEFAULT'>
        <div className='container mx-auto px-4 py-20'>
          <div className='text-center py-10'>
            <div className='inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-DEFAULT'></div>
            <p className='mt-4 text-lg'>Loading member profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className='container mx-auto px-4 py-20'>
        <div className='bg-white dark:bg-dark-light rounded-xl shadow-xl p-8 text-center'>
          <h2 className='text-2xl font-bold mb-4'>Error</h2>
          <p className='mb-6'>{error || 'Member not found'}</p>
          <Link
            to='/members'
            className='inline-block bg-primary-DEFAULT hover:bg-primary-dark  font-bold py-2 px-6 rounded-full transition-colors duration-300'
          >
            Back to Members
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className='container mx-auto px-4 py-20'>
      <Helmet>
        <title>{member.name} - {member.title} | Red Panda Treehouse</title>
        <meta name="description" content={member.description || `Meet ${member.name}, ${member.title} from Red Panda Treehouse. Discover their content and connect with them.`} />
        <meta name="keywords" content={`${member.name}, red panda vtuber, ${member.title}, vtuber profile, streaming`} />
        <link rel="canonical" href={`https://redpandatreehouse.com/members/${memberId}`} />

        {/* Open Graph */}
        <meta property="og:title" content={`${member.name} - ${member.title}`} />
        <meta property="og:description" content={member.description || `Meet ${member.name}, ${member.title} from Red Panda Treehouse.`} />
        <meta property="og:url" content={`https://redpandatreehouse.com/members/${memberId}`} />
        <meta property="og:type" content="profile" />
        <meta property="og:image" content={`https://redpandatreehouse.com${member.image}`} />

        {/* Twitter */}
        <meta name="twitter:title" content={`${member.name} - ${member.title}`} />
        <meta name="twitter:description" content={member.description || `Meet ${member.name}, ${member.title} from Red Panda Treehouse.`} />
        <meta name="twitter:image" content={`https://redpandatreehouse.com${member.image}`} />

        {/* Structured Data - Person Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": member.name,
            "jobTitle": member.title,
            "description": member.description || `${member.name} is a ${member.title} from Red Panda Treehouse`,
            "image": `https://redpandatreehouse.com${member.image}`,
            "url": `https://redpandatreehouse.com/members/${memberId}`,
            "memberOf": {
              "@type": "Organization",
              "name": "Red Panda Treehouse",
              "url": "https://redpandatreehouse.com"
            },
            "sameAs": member.links ? member.links.map(link => link.url) : []
          })}
        </script>
      </Helmet>
      <div className='mb-8'>
        <Link
          to='/members'
          className='inline-flex items-center text-primary-DEFAULT hover:text-primary-dark transition-colors'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            className='h-5 w-5 mr-2'
            viewBox='0 0 20 20'
            fill='currentColor'
          >
            <path
              fillRule='evenodd'
              d='M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z'
              clipRule='evenodd'
            />
          </svg>
          Back to Members
        </Link>
      </div>

      <div className='bg-white dark:bg-dark-light rounded-xl shadow-xl p-8 animate-fade-in'>
        <div className='flex flex-col md:flex-row items-start gap-8'>
          <div className='md:w-1/3'>
            <img
              src={member.image}
              alt={member.name}
              className='rounded-lg w-full shadow-xl'
              onError={(e) => {
                e.target.src =
                  'https://via.placeholder.com/600x800?text=No+Image';
              }}
            />
          </div>
          <div className='md:w-2/3'>
            <h1 className='text-4xl font-bold mb-2'>{member.name}</h1>
            <p className='text-primary-DEFAULT text-xl mb-4'>{member.title}</p>

            <div className='mb-6'>
              <h2 className='text-2xl font-bold mb-2'>About</h2>
              <p className='whitespace-pre-line'>
                {member.description || 'No description available.'}
              </p>
            </div>

            {member.links && member.links.length > 0 && (
              <div className='mb-6'>
                <h2 className='text-2xl font-bold mb-4'>Links</h2>
                <div className='flex flex-wrap gap-4'>
                  {member.links.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='flex items-center px-4 py-2 bg-gray-100 dark:bg-dark-light rounded-full hover:bg-gray-200 dark:hover:bg-dark-dark transition-colors'
                    >
                      {link.iconImage && (
                        <FontAwesomeIcon
                          icon={getIconFromString(link.iconImage)}
                          className='mr-2'
                        />
                      )}
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
export default MemberDetail;
